import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import Modal from '../../../../components/Modal/Modal';
import Text from '../../../../components/Text/Text';
import Input from '../../../../components/Input/Input';
import Checkbox from '../../../../components/Checkbox/Checkbox';
import toggleArrayItem from '../../../../helpers/toggleArrayItem';
import postRoles from '../../../../thunks/roles/postRoles';
import patchRoles from '../../../../thunks/roles/patchRoles';
import Alert from '../../../../components/Alert/Alert';
import AlertType from '../../../../common/alertType';
import { permissionGroup, permissionTypes } from '../../../../common/permissionGroup';

const modalContentClassName = 'max-h-[357px] w-[650px] overflow-y-auto p-[22px]';
const modalTextClassName = 'text-[15px]';
const inputContainerClassName = 'mb-[30px] mt-[10px]';
const inputClassName = 'py-[9px] px-[13px]';
const permissionsContainerClassName = '';
const rowClassName = 'flex flex-row border-b border-dashed border-whiteHover last:border-b-0 py-[16px] pr-[9px]';
const rowTextClassName = 'capitalize flex-1';
const rowCheckBoxContainerClassName = 'flex-[2] flex justify-between';
const rowCheckBoxClassName = 'flex';

const RoleModal = ({
  visible, toggleVisible, editRole, activePermissions, setActivePermissions
}) => {
  const dispatch = useDispatch();
  const {
    getPermissions: {
      permissionsObject = {},
    } = {},
    postRoles: { error, loading: inProgress, createId } = {},
  } = useSelector((state) => ({
    getPermissions: state?.getPermissions,
    postRoles: state?.postRoles
  }), shallowEqual);

  useEffect(() => {
    if (createId && visible) {
      toggleVisible();
    }
  }, [createId]);

  const {
    values: {
      name
    },
    handleSubmit,
    handleChange,
    handleBlur,
    errors,
    isValid,
    touched,
    setFieldValue
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
    },
    onSubmit: (values) => {
      if (editRole) {
        dispatch(patchRoles({
          id: editRole.id,
          name: values.name.toLowerCase(),
          permissions: activePermissions
        }));
      } else {
        dispatch(postRoles({
          name: values.name.toLowerCase(),
          permissions: activePermissions
        }));
      }
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required('Required').min(4),
    })
  });

  useEffect(() => {
    if (editRole) {
      const { name: editName, role_permission: editRolePermission } = editRole;
      setFieldValue('name', editName);
      setActivePermissions(editRolePermission.map(({ id }) => id));
    } else {
      setFieldValue('name', '');
    }
  }, [editRole]);
  const isAdminRole = editRole?.role_permission?.find(
    ({ group, permission }) => group === permissionGroup.ADMIN && permission === permissionTypes.ADMIN
  );

  return (
    <Modal
      visible={visible}
      toggleVisible={toggleVisible}
      title={`${editRole ? 'Update' : 'Add a'} Role`}
      onSave={handleSubmit}
      canSave={!(isValid && !!activePermissions.length && !inProgress)}
      saveText={!editRole ? 'Create Role' : undefined}
    >
      {error && <Alert type={AlertType.warning} text={error} />}
      <div className={`${modalContentClassName} customScroll`}>
        <Text text="Role name" bold textStyle={modalTextClassName} required />
        <Input
          type="text"
          name="name"
          value={name}
          placeholder="Enter a role name"
          onBlur={handleBlur}
          onChange={handleChange}
          errorText={touched?.name && errors?.name}
          inputStyle={inputClassName}
          containerStyle={inputContainerClassName}
        />
        {!isAdminRole && (
          <>
            <Text text="Role Permissions" bold textStyle={modalTextClassName} required />
            <div className={permissionsContainerClassName}>
              {Object.entries(permissionsObject).map(([key, value]) => (
                <div className={rowClassName} key={key}>
                  <Text
                    text={`${key} Management`}
                    styleContainer={rowTextClassName}
                  />
                  <div className={rowCheckBoxContainerClassName}>
                    {value.map(({ id, permission }) => (
                      <div className={rowCheckBoxClassName} key={id}>
                        <Checkbox
                          name={`${permission}_${id}`}
                          text={permission}
                          onChange={() => setActivePermissions(toggleArrayItem(activePermissions, id))}
                          isActive={!!activePermissions.find((item) => item === id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

RoleModal.propTypes = {
  toggleVisible: PropTypes.func,
  setActivePermissions: PropTypes.func,
  visible: PropTypes.bool,
  editRole: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    role_permission: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  activePermissions: PropTypes.arrayOf(PropTypes.number)
};

RoleModal.defaultProps = {
  toggleVisible: () => {},
  setActivePermissions: () => {},
  visible: false,
  editRole: undefined,
  activePermissions: []
};

export default RoleModal;
